<app-screen-shell contentWidth="standard" contentInset="page" [contentClass]="'notifications-screen'" [showHeader]="false">
  <header class="notifications-toolbar" [class.has-actions]="hasNotifications$ | async" [attr.aria-label]="'notifications.centerTitle' | translate">
    <div class="notifications-filters" role="tablist" [attr.aria-label]="'notifications.filters.label' | translate">
      <button type="button" class="notifications-filter" [class.is-active]="activeFilter === 'all'" (click)="setFilter('all')">
        {{ 'common.all' | translate }}
      </button>
      <button type="button" class="notifications-filter" [class.is-active]="activeFilter === 'match'" (click)="setFilter('match')">
        {{ 'events.match' | translate }}
      </button>
      <button type="button" class="notifications-filter" [class.is-active]="activeFilter === 'manager'" (click)="setFilter('manager')">
        {{ 'common.manager' | translate }}
      </button>
      <button type="button" class="notifications-filter" [class.is-active]="activeFilter === 'account'" (click)="setFilter('account')">
        {{ 'players.profileMenuAccount' | translate }}
      </button>
    </div>
    @if (hasNotifications$ | async) {
      <button
        type="button"
        class="notifications-dismiss-all"
        [attr.aria-label]="'notifications.dismissAll' | translate"
        [title]="'notifications.dismissAll' | translate"
        (click)="requestDismissAll()">
        <i [class]="'notifications.readAll' | nogoshIcon" aria-hidden="true"></i>
      </button>
    }
  </header>

  @if (actionMessage) {
    <p class="alert alert-success py-2" role="alert">{{ actionMessage }}</p>
  }
  @if (actionError) {
    <p class="alert alert-danger py-2" role="alert">{{ actionError }}</p>
  }

  <ng-container *ngIf="notifications$ | async as notifications">
    @if (filteredNotifications(notifications); as visibleNotifications) {
    @if (confirmDismissAll && visibleNotifications.length > 0) {
      <div class="notification-confirm-all" role="alertdialog" [attr.aria-label]="'notifications.dismissAll' | translate">
        <div>
          <strong>{{ 'notifications.dismissAll' | translate }}</strong>
          <p class="mb-0">{{ 'notifications.dismissAllConfirmDescription' | translate:{count: visibleNotifications.length} }}</p>
        </div>
        <div class="notification-confirm-actions">
          <button type="button" class="btn btn-outline-secondary btn-sm px-3" (click)="cancelDismissAll()">
            {{ 'common.cancel' | translate }}
          </button>
          <button type="button" class="btn btn-danger btn-sm px-3" (click)="dismissAllNotifications(visibleNotifications)">
            {{ 'common.yes' | translate }}
          </button>
        </div>
      </div>
    }

    @if (notifications.length === 0 && (loading$ | async)) {
      <div class="loading-state loading-state-page" role="status" aria-live="polite">
        <div class="loading-state-mark" aria-hidden="true">
          <span class="loading-state-ball">⚽</span>
        </div>
        <p class="loading-state-title">{{ 'common.loadingApp' | translate }}</p>
        <p class="loading-state-subtitle">{{ 'common.loadingHint' | translate }}</p>
      </div>
    }

    @if (notifications.length === 0 && !(loading$ | async)) {
      <div class="card notification-empty-card">
        <div class="card-body">
          <h3 class="mb-2">{{ 'notifications.emptyTitle' | translate }}</h3>
          <p class="text-nogosh-grey3 mb-0">{{ 'notifications.emptyDescription' | translate }}</p>
        </div>
      </div>
    }

    @if (notifications.length > 0 && visibleNotifications.length === 0) {
      <div class="card notification-empty-card">
        <div class="card-body">
          <h3 class="mb-2">{{ 'notifications.filters.empty' | translate }}</h3>
          <p class="text-nogosh-grey3 mb-0">{{ 'notifications.emptyDescription' | translate }}</p>
        </div>
      </div>
    }

    @if (visibleNotifications.length > 0) {
      <div class="notification-list">
        @for (notification of visibleNotifications; track notification.id) {
          <div class="card notification-card" [class.is-unread]="!notification.read">
            <div class="card-body">
              <div class="notification-icon" aria-hidden="true">
                <i [class]="notification.iconClass"></i>
              </div>
              <div class="notification-content">
                <div class="notification-main-row">
                  <div class="notification-heading">
                    <h3 class="mb-0">{{ notification.titleKey | translate:notificationParams(notification) }}</h3>
                    @if (notification.createdAt) {
                      <div class="notification-time">
                        @if (isOlderThanWeek(notification)) {
                          {{ notification.createdAt | localizedDate:'EEE, d MMM yyyy, HH:mm' }}
                        } @else {
                          {{ notification.createdAt | localizedDate:'EEEE, HH:mm' }}
                        }
                      </div>
                    }
                  </div>
                  <button
                    type="button"
                    class="notification-dismiss"
                    [attr.aria-label]="'notifications.dismiss' | translate"
                    [title]="'notifications.dismiss' | translate"
                    (click)="dismissNotification(notification)">
                    <i [class]="'common.check' | nogoshIcon" aria-hidden="true"></i>
                  </button>
                </div>
                <p class="text-nogosh-grey3 mb-0">
                  @if (notification.type === 'missingResult' && notification.event) {
                    {{ notification.event.name }} -
                    {{ notification.event.nextOccurrence || notification.event.nextOccurrenceTimestamp ? ((notification.event.nextOccurrence || notification.event.nextOccurrenceTimestamp) | localizedDate:'EEEE, d MMMM @ HH:mm') : ('events.timeNotSet' | translate) }}
                  } @else if (notification.type === 'ratingReview' && notification.ratingReview) {
                    {{ 'notifications.ratingReviewDescription' | translate:{count: notification.ratingReview.playersRemaining, match: notification.ratingReview.event.name} }}
                  } @else {
                    {{ notification.descriptionKey | translate:notificationParams(notification) }}
                  }
                </p>
                <div class="notification-actions">
                  <button
                    type="button"
                    class="btn btn-primary btn-sm px-3"
                    [disabled]="!!actionLoadingId"
                    (click)="handleNotification(notification)">
                    @if (actionLoadingId === notification.id) {
                      <i [class]="('common.loading' | nogoshIcon) + ' nogosh-icon-spin mr-1'" aria-hidden="true"></i>
                    }
                    {{ notificationActionKey(notification) | translate }}
                  </button>
                  @if (notification.type === 'ratingReview') {
                    <button
                      type="button"
                      class="btn btn-link btn-sm px-3"
                      [disabled]="!!actionLoadingId"
                      (click)="remindRatingReviewLater(notification)">
                      {{ 'notifications.remindLater' | translate }}
                    </button>
                  }
                </div>
              </div>
            </div>
          </div>
        }
      </div>
    }
    }
  </ng-container>
</app-screen-shell>
